import { apiClient } from "@/lib/api";
import { fetchAllPages } from "@/lib/fetchAllPages";
import type {
  House,
  HouseCreate,
  HouseUpdate,
  HouseAdminUpdate,
  HouseListResponse,
  HouseWithDeviceCount,
  DevicePresence,
  DevicePresenceCreate,
  DevicePresenceUpdate,
  HouseCurrentPresenceItem,
  HouseDevice,
} from "@/types";

/** Backend cap: GET /houses/by-host/{host_id} `limit` le=100. */
const HOUSES_BY_HOST_PAGE_SIZE = 100;

export interface PresenceImportPreviewItem {
  device_id: string | null;
  source_room_label: string | null;
  guest_name: string | null;
  nationality: string | null;
  arrival_date: string | null;
  departure_date: string | null;
}

export interface PresenceImportConfirmItem {
  device_id: string;
  guest_name: string;
  nationality: string;
  arrival_date: string;
  departure_date: string;
}

export interface PresenceImportPreviewResponse {
  items: PresenceImportPreviewItem[];
}

export const housePresenceKeys = {
  all: (houseId: string) => ["house-presences", houseId] as const,
  current: (houseId: string) =>
    ["house-presences-current", houseId] as const,
  upcoming: (houseId: string) =>
    ["house-presences-upcoming", houseId] as const,
  history: (
    houseId: string,
    range?: { dateFrom: string; dateTo: string },
  ) =>
    range
      ? ([
          "house-presences-history",
          houseId,
          range.dateFrom,
          range.dateTo,
        ] as const)
      : (["house-presences-history", houseId] as const),
};

export const houseService = {
  /**
   * Create a new house
   */
  async create(data: HouseCreate): Promise<House> {
    const response = await apiClient.post<House>("/houses", data);
    return response.data;
  },

  /**
   * Get a house by ID with device count
   */
  async getById(houseId: string): Promise<HouseWithDeviceCount> {
    const response = await apiClient.get<HouseWithDeviceCount>(
      `/houses/${houseId}`,
    );
    return response.data;
  },

  /**
   * Update a house (host can update name/address)
   */
  async update(houseId: string, data: HouseUpdate): Promise<House> {
    const response = await apiClient.patch<House>(`/houses/${houseId}`, data);
    return response.data;
  },

  /**
   * Admin update for a house (includes commission rate)
   */
  async adminUpdate(houseId: string, data: HouseAdminUpdate): Promise<House> {
    const response = await apiClient.patch<House>(
      `/houses/${houseId}/admin`,
      data,
    );
    return response.data;
  },

  /** Every house of a host (all pages). */
  listByHost(hostId: string): Promise<House[]> {
    return fetchAllPages(async (page) => {
      const response = await apiClient.get<HouseListResponse>(
        `/houses/by-host/${hostId}`,
        { params: page },
      );
      return response.data.items;
    }, HOUSES_BY_HOST_PAGE_SIZE);
  },

  /**
   * List devices in a house
   */
  async listDevices(houseId: string): Promise<HouseDevice[]> {
    const response = await apiClient.get<HouseDevice[]>(
      `/houses/${houseId}/devices`,
    );
    return response.data;
  },

  /**
   * Persist display order for all devices currently in a house.
   */
  async reorderDevices(
    houseId: string,
    deviceIds: string[],
  ): Promise<HouseDevice[]> {
    const response = await apiClient.put<HouseDevice[]>(
      `/houses/${houseId}/devices/reorder`,
      { device_ids: deviceIds },
    );
    return response.data;
  },

  /**
   * Update a device in a house (hosts may only rename; admins may update device state).
   */
  async updateDevice(
    houseId: string,
    deviceId: string,
    data: {
      is_active?: boolean;
      display_name?: string;
    },
  ): Promise<HouseDevice> {
    const response = await apiClient.patch<HouseDevice>(
      `/houses/${houseId}/devices/${deviceId}`,
      data,
    );
    return response.data;
  },

  /**
   * Reset device password. Returns new password (shown only once).
   */
  async resetDevicePassword(
    houseId: string,
    deviceId: string,
  ): Promise<{ password: string }> {
    const response = await apiClient.post<{ password: string }>(
      `/houses/${houseId}/devices/${deviceId}/reset-password`,
    );
    return response.data;
  },

  /**
   * List presences for a device (filter: current, future, or all).
   */
  async listDevicePresences(
    houseId: string,
    deviceId: string,
    filter?: "current" | "future" | "all",
  ): Promise<DevicePresence[]> {
    const params = filter ? { filter } : {};
    const response = await apiClient.get<DevicePresence[]>(
      `/houses/${houseId}/devices/${deviceId}/presences`,
      { params },
    );
    return response.data;
  },

  /**
   * Create a new presence for a device.
   */
  async createDevicePresence(
    houseId: string,
    deviceId: string,
    data: DevicePresenceCreate,
    options?: { replaceCurrent?: boolean },
  ): Promise<DevicePresence> {
    const params = options?.replaceCurrent ? { replace_current: true } : undefined;
    const response = await apiClient.post<DevicePresence>(
      `/houses/${houseId}/devices/${deviceId}/presences`,
      data,
      { params },
    );
    return response.data;
  },

  /**
   * Update a presence.
   */
  async updateDevicePresence(
    houseId: string,
    deviceId: string,
    presenceId: string,
    data: DevicePresenceUpdate,
  ): Promise<DevicePresence> {
    const response = await apiClient.put<DevicePresence>(
      `/houses/${houseId}/devices/${deviceId}/presences/${presenceId}`,
      data,
    );
    return response.data;
  },

  /**
   * Delete a presence.
   */
  async deleteDevicePresence(
    houseId: string,
    deviceId: string,
    presenceId: string,
  ): Promise<void> {
    await apiClient.delete(
      `/houses/${houseId}/devices/${deviceId}/presences/${presenceId}`,
    );
  },

  async listCurrentPresences(
    houseId: string,
  ): Promise<HouseCurrentPresenceItem[]> {
    const response = await apiClient.get<HouseCurrentPresenceItem[]>(
      `/houses/${houseId}/presences/current`,
    );
    return response.data;
  },

  async listUpcomingPresences(
    houseId: string,
  ): Promise<HouseCurrentPresenceItem[]> {
    const response = await apiClient.get<HouseCurrentPresenceItem[]>(
      `/houses/${houseId}/presences/upcoming`,
    );
    return response.data;
  },

  async listPeriodPresences(
    houseId: string,
    params?: {
      date_from?: string;
      date_to?: string;
    },
  ): Promise<HouseCurrentPresenceItem[]> {
    const response = await apiClient.get<HouseCurrentPresenceItem[]>(
      `/houses/${houseId}/presences/history`,
      { params },
    );
    return response.data;
  },

  async extractPresencesFromPdf(
    houseId: string,
    file: File,
  ): Promise<PresenceImportPreviewResponse> {
    const form = new FormData();
    form.append("file", file);
    const response = await apiClient.post<PresenceImportPreviewResponse>(
      `/houses/${houseId}/presences/import/extract`,
      form,
    );
    return response.data;
  },

  async confirmPresenceImport(
    houseId: string,
    items: PresenceImportConfirmItem[],
  ): Promise<DevicePresence[]> {
    const response = await apiClient.post<DevicePresence[]>(
      `/houses/${houseId}/presences/import/confirm`,
      { items },
    );
    return response.data;
  },
};
